export interface RosterFormat {
  id: string;
  name: string;
  description: string;
  roster: {
    QB: number;
    RB: number;
    WR: number;
    TE: number;
    K: number;
    DEF: number;
    FLEX: number;
    SUPERFLEX?: number;
    BENCH: number;
  };
  starters: {
    QB: number;
    RB: number;
    WR: number;
    TE: number;
    K: number;
    DEF: number;
    FLEX: number;
    SUPERFLEX?: number;
  };
  scoring: {
    rec: number; // PPR value
    passTd: number;
    rushTd: number;
    recTd: number;
  };
  teamCount: number;
}

export interface UserRoster {
  id: string;
  name: string;
  format: RosterFormat;
  players: string[]; // Player IDs
  starters: string[]; // Player IDs of players designated as starters
  customStarterLimits?: { // Override default starter limits
    QB?: number;
    RB?: number;
    WR?: number;
    TE?: number;
    K?: number;
    DEF?: number;
    FLEX?: number;
    SUPERFLEX?: number;
  };
  createdAt: Date;
  updatedAt: Date;
  isActive: boolean;
}

export const ROSTER_FORMATS: Record<string, RosterFormat> = {
  standard: {
    id: 'standard',
    name: 'Standard',
    description: '1 QB, 2 RB, 2 WR, 1 TE, 1 FLEX, 1 K, 1 D/ST',
    roster: {
      QB: 3,
      RB: 6,
      WR: 6,
      TE: 3,
      K: 2,
      DEF: 2,
      FLEX: 1,
      BENCH: 6
    },
    starters: {
      QB: 1,
      RB: 2,
      WR: 2,
      TE: 1,
      K: 1,
      DEF: 1,
      FLEX: 1
    },
    scoring: {
      rec: 0.5, // Half PPR
      passTd: 4,
      rushTd: 6,
      recTd: 6
    },
    teamCount: 12
  },
  superflex: {
    id: 'superflex',
    name: 'Superflex',
    description: '1 QB, 2 RB, 2 WR, 1 TE, 1 FLEX, 1 SUPERFLEX, 1 K, 1 D/ST',
    roster: {
      QB: 4,
      RB: 6,
      WR: 6,
      TE: 3,
      K: 2,
      DEF: 2,
      FLEX: 1,
      SUPERFLEX: 1,
      BENCH: 6
    },
    starters: {
      QB: 1,
      RB: 2,
      WR: 2,
      TE: 1,
      K: 1,
      DEF: 1,
      FLEX: 1,
      SUPERFLEX: 1
    },
    scoring: {
      rec: 0.5,
      passTd: 4,
      rushTd: 6,
      recTd: 6
    },
    teamCount: 12
  },
  bestball: {
    id: 'bestball',
    name: 'Best Ball',
    description: '1 QB, 2 RB, 3 WR, 1 TE, 1 FLEX, no K/D',
    roster: {
      QB: 3,
      RB: 7,
      WR: 8,
      TE: 3,
      K: 0,
      DEF: 0,
      FLEX: 1,
      BENCH: 0
    },
    starters: {
      QB: 1,
      RB: 2,
      WR: 3,
      TE: 1,
      K: 0,
      DEF: 0,
      FLEX: 1
    },
    scoring: {
      rec: 0.5,
      passTd: 4,
      rushTd: 6,
      recTd: 6
    },
    teamCount: 12
  },
  twoqb: {
    id: 'twoqb',
    name: '2QB',
    description: '2 QB, 2 RB, 2 WR, 1 TE, 1 FLEX, 1 K, 1 D/ST',
    roster: {
      QB: 4,
      RB: 6,
      WR: 6,
      TE: 3,
      K: 2,
      DEF: 2,
      FLEX: 1,
      BENCH: 6
    },
    starters: {
      QB: 2,
      RB: 2,
      WR: 2,
      TE: 1,
      K: 1,
      DEF: 1,
      FLEX: 1
    },
    scoring: {
      rec: 0.5,
      passTd: 4,
      rushTd: 6,
      recTd: 6
    },
    teamCount: 12
  },
  dynasty: {
    id: 'dynasty',
    name: 'Dynasty',
    description: '1 QB, 2 RB, 2 WR, 1 TE, 4 FLEX, no K/D',
    roster: {
      QB: 4,
      RB: 8,
      WR: 8,
      TE: 4,
      K: 0,
      DEF: 0,
      FLEX: 4,
      SUPERFLEX: 1,
      BENCH: 15
    },
    starters: {
      QB: 1,
      RB: 2,
      WR: 2,
      TE: 1,
      K: 0,
      DEF: 0,
      FLEX: 4,
      SUPERFLEX: 1
    },
    scoring: {
      rec: 1.0, // Full PPR common in dynasty
      passTd: 4,
      rushTd: 6,
      recTd: 6
    },
    teamCount: 12
  }
};